import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useDealContext } from '@/lib/contexts/deal.context';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { useValidateDealSelection } from '@/lib/hooks/useValidateDealSelection';
import {
  getDomainErrorMessage,
  getErrorDomainCode,
} from '@/lib/utils/domain-error-translator';
import { shouldHideShippingPolicyDialog } from '@/lib/utils/shipping-policy-utils';
import { validateBrandButtonAtom } from '@/states/validate-brand-button.state';
import { ShippingMode } from '@/types/shipping';
import { useAtomValue } from 'jotai';
import { Send } from 'lucide-react';

import BrandValidationDialog from './brand-validation-dialog';
import ShippingPolicyDialog from './shipping-policy/shipping-policy-dialog';

type OpenModal = null | 'shipping_policy' | 'brand_confirm';

function ValidateOfferBrand() {
  const { t } = useTranslation();
  const {
    deal: { id: dealId },
  } = useDealContext();
  const isOfferLocked = useIsOfferLocked();
  const { shouldRender, isDisabledFromData } = useAtomValue(
    validateBrandButtonAtom
  );
  const { shippingPolicy } = useShippingPolicy(dealId);
  const { toast } = useToast();
  const { validateDealSelection } = useValidateDealSelection(dealId);

  const [openModal, setOpenModal] = useState<OpenModal>(null);

  if (!shouldRender) {
    return null;
  }

  const isValidationDisabled = isDisabledFromData || isOfferLocked;

  const handleBrandValidation = async () => {
    setOpenModal(null);
    try {
      await validateDealSelection();
    } catch (error) {
      toast({
        title: t('general.error'),
        description: getDomainErrorMessage(
          getErrorDomainCode(error),
          t,
          'components.validate_offer_button.validate_offer_error'
        ),
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: t('page.validation_toast_title'),
      description: t('page.validation_toast_message'),
      variant: 'default',
    });
  };

  const handleValidateButtonClick = () => {
    const hasShippingPolicy = shouldHideShippingPolicyDialog({
      shippingPolicy,
      mode: ShippingMode.Validate,
    });
    if (hasShippingPolicy) {
      setOpenModal('brand_confirm');
    } else {
      setOpenModal('shipping_policy');
    }
  };

  const handleCloseModal = (open: boolean) => {
    if (!open) {
      setOpenModal(null);
    }
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip open={isValidationDisabled ? undefined : false}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              data-user-guide="Validate"
              variant="default"
              className="flex items-center gap-2"
              onClick={handleValidateButtonClick}
              disabled={isValidationDisabled}
            >
              <Send className="h-4 w-4" />
              {t('components.validate_offer_button.validate_and_send_offer')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('components.offer_actions_header.offer_validation_tooltip')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {openModal === 'shipping_policy' && (
        <ShippingPolicyDialog
          open
          onOpenChange={handleCloseModal}
          dealId={dealId}
          onConfirm={() => setOpenModal('brand_confirm')}
          mode={ShippingMode.Validate}
        />
      )}
      {openModal === 'brand_confirm' && (
        <BrandValidationDialog
          open
          onOpenChange={handleCloseModal}
          onConfirm={handleBrandValidation}
        />
      )}
    </>
  );
}

export default ValidateOfferBrand;
